/**
 * Work Orders Action API Contract
 *
 * ts-rest contract definitions for bulk work-order action endpoints.
 *
 * Endpoints:
 *   POST /work-orders/accept           → accept multiple work orders by ID
 *   POST /work-orders/reject           → reject work orders with per-job comments
 *   POST /work-orders/request-more-info → request info with per-job comments
 *
 * Response shape for all three is a per-job results array so the client can
 * surface partial-success outcomes (some jobs succeeded, some failed). The API
 * always returns 200 OK — individual failures are reported via the `results`
 * entries, not the HTTP status.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

/**
 * Legacy request body — used by the accept endpoint where no per-job input is
 * needed, and retained on the contract for consumers that haven't migrated.
 */
export const workOrderIdsBodySchema = z.object({
  ids: z.array(z.number().int()),
});

/**
 * Per-job input for reject / request-more-info. Each selected job carries its
 * own free-text comments so the user can explain context job-by-job.
 */
export const workOrderItemInputSchema = z.object({
  id: z.number().int(),
  comments: z.string().min(1).max(500),
});

export const workOrderItemsBodySchema = z.object({
  items: z.array(workOrderItemInputSchema).min(1),
});

/**
 * Per-job result row. `success=true` means the transition landed in the DB;
 * `success=false` means the job was skipped or failed and `message` explains
 * why (e.g. "Status transition not allowed", "Job already rejected").
 */
export const workOrderActionResultSchema = z.object({
  id: z.number().int(),
  success: z.boolean(),
  message: z.string().optional(),
});

export const workOrderActionResponseSchema = z.object({
  results: z.array(workOrderActionResultSchema),
});

export type WorkOrderIdsBody = z.infer<typeof workOrderIdsBodySchema>;
export type WorkOrderItemInput = z.infer<typeof workOrderItemInputSchema>;
export type WorkOrderItemsBody = z.infer<typeof workOrderItemsBodySchema>;
export type WorkOrderActionResult = z.infer<typeof workOrderActionResultSchema>;
export type WorkOrderActionResponse = z.infer<typeof workOrderActionResponseSchema>;

/**
 * Work Orders Action Contract
 *
 * Base path: /work-orders  (mounted in apps/backend/src/routes/index.ts)
 */
